<template>
    <span v-if="display">
        {{ selectedItem ? getItemText(selectedItem) : '' }}
    </span>
    <app-autocomplete
        v-else
        ref="input"
        v-model="selected"
        :test-id="testId"
        name="search"
        :hide-details="hideDetails"
        class="field-search"
        :items="items"
        :loading="setupLoading || searchLoading || loading || loadingMore"
        :disabled="setupLoading || disabled"
        :readonly="readonly"
        :clearable="!readonly && !disabled && clearable"
        :label="labelValue"
        :placeholder="placeholderValue"
        return-object
        :item-text="itemText"
        :item-value="itemKey"
        :required="required"
        :rules="rulesValue"
        :error-messages="errorMessagesValue"
        color="primaryText"
        item-color="primaryText"
        outlined
        dense
        cache-items
        :search-input.sync="searchCriteria"
        :menu-props="searchMenuProps"
        @input="item => onItemSelected(item)"
        @click:clear="onItemSelected()"
        @mousedown="onMouseDown"
        @keypress.enter.prevent
    >
        <template #no-data>
            <v-list-item>
                <v-list-item-content>
                    <v-list-item-title>
                        <text-animation-loader v-if="showNoDataLoader" :prepend="noDataLoaderLabel" loading spinner />
                        <span v-else-if="showNoResultsMessage">{{ $t('no_results_found') }}</span>
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
        </template>

        <template #item="{ item }">
            <div
                v-intersect="(entries, observer, isIntersecting) => isIntersecting && onIntersect(item)"
                v-test-id="{ page: labelValue, field: getItemText(item), action: 'option' }"
                class="field-search__item"
            >
                <app-tooltip :value="getItemTooltip(item)" :disabled="!getItemTooltip(item)">
                    <v-list-item-content>
                        <v-list-item-title class="body-2">
                            {{ getItemText(item) }}
                        </v-list-item-title>
                        <v-list-item-subtitle
                            v-if="getItemDescription(item)"
                            class="field-search__item-description text--secondary"
                        >
                            {{ getItemDescription(item) }}
                        </v-list-item-subtitle>
                    </v-list-item-content>
                </app-tooltip>
            </div>
        </template>

        <template #append-outer>
            <slot name="append-outer" />

            <template v-if="allowCreate && createForm">
                <app-button
                    :test-id="'button-create_' + $options.name"
                    :disabled="disabled || readonly || initialising"
                    :icon="getIcon('add')"
                    :class="{ 'mb-7': !hideDetails, 'mt-2': hideDetails }"
                    :tooltip="disabled || readonly || initialising ? null : $t('create') + ' ' + labelValue"
                    @click.stop="createModal = true"
                />

                <app-dialog
                    v-if="!initialising"
                    v-model="createModal"
                    :size="createModalSize"
                    :title="`${$t('create')} ${labelValue}`"
                    :text="`The new ${labelValue.toLowerCase()} will be automatically selected when created.`"
                    :continue-button-text="$t('create') + ' ' + labelValue"
                    :hide-on-continue="false"
                    :on-continue-fn="() => $refs.form.$refs.form.onSubmit()"
                >
                    <component
                        :is="createForm"
                        ref="form"
                        hide-header
                        hide-actions
                        class="pt-4"
                        @input:response="onCreated"
                    />
                </app-dialog>
            </template>
        </template>

        <template #append>
            <slot name="append" />
        </template>
    </app-autocomplete>
</template>

<script>
import { getCurrentInstance } from 'vue';

import { usePaginatedCollection } from '@composables/usePaginatedCollection';

import ErrorService from '@core/services/ErrorService';

import { Collection } from '@/models';

import BaseField from './BaseField.vue';

export default {
    name: 'Search',
    components: {
        AppDialog: () => import('@/components/AppDialog.vue')
    },
    extends: BaseField,
    props: {
        collection: {
            type: Collection,
            default: null
        },
        value: {
            type: String,
            default: null
        },
        itemKey: {
            type: String,
            default: 'id'
        },
        itemText: {
            type: [String, Function],
            default: 'name'
        },
        itemDescription: {
            type: [String, Function],
            default: null
        },
        itemTooltip: {
            type: [String, Function],
            default: null
        },
        presetItems: {
            type: Array,
            default: null
        },
        returnObject: {
            type: Boolean,
            default: false
        },
        filters: {
            type: Object,
            default: () => ({})
        },
        allowCreate: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: true
        },
        immediate: {
            type: Boolean,
            default: false
        },
        autoSelect: {
            type: Boolean,
            default: true
        }
    },
    // Vue merges data() across `extends`, but not setup() — a subclass declaring its own
    // setup() replaces this one and loses pagination.
    setup() {
        const { proxy } = getCurrentInstance();

        return usePaginatedCollection({
            fetchPage: (offset, limit) =>
                proxy.collection.request(offset, limit, [], { ...proxy.filters, ...proxy.presetFilters }),
            onPage: rows => {
                proxy.items.push(...rows);
                proxy.pushCachedItems(rows);
            },
            onError: (error, retry) => proxy.handleError(error, retry),
            getItems: () => proxy.items,
            getItemKey: item => item?.[proxy.itemKey],
            enabled: () => !proxy.presetItems && !proxy.searching
        });
    },
    data: () => ({
        setupOnMounted: false,
        initialising: false,
        selected: null,

        items: [],
        initialItems: [],
        presetFilters: {},

        createForm: null,
        createModal: false,
        createModalSize: 'xx-large',

        searchCriteria: null,
        searchLoading: false,
        searching: false,
        selectedItemText: null,
        searchDebounced: () => {},
        searchRequests: [],
        pendingSearchCriteria: undefined
    }),
    computed: {
        showNoDataLoader() {
            return this.initialising || this.searchLoading || this.setupLoading || this.loading || this.loadingMore;
        },
        noDataLoaderLabel() {
            if (this.initialising) {
                return this.$t('initialising');
            }

            return this.$t('searching');
        },
        showNoResultsMessage() {
            return !this.showNoDataLoader;
        },
        selectedItem() {
            return this.items.find(item => item[this.itemKey] === this.value);
        },
        selectedText() {
            // Recorded at selection, because a row picked out of search results is not in
            // initialItems and could not be resolved from it.
            if (this.selectedItemText) {
                return this.selectedItemText;
            }

            const item = this._.find(this.initialItems, [this.itemKey, this.value]);

            return item ? this.getItemText(item) : null;
        },
        searchMenuProps() {
            return {
                contentClass: 'field-search__menu',
                maxWidth: 512,
                ...this.menuProps
            };
        }
    },
    watch: {
        initialising(initialising) {
            if (!initialising) {
                this.flushPendingSearchCriteria();
            }
        },
        value: {
            immediate: true,
            handler(value) {
                if (value && !this.setupLoaded) {
                    this.init();
                }
            }
        },
        inputValue(value) {
            if (value === null) {
                this.selected = null;
            } else if (value !== this.selected) {
                this.selected = value;
            }
        },
        searchCriteria(criteria) {
            if (this.presetItems) {
                return;
            }

            // If we're still initialising this has probably been triggered
            // by the item population triggering the search-input, so
            // we won't run the search in this case
            if (this.initialising) {
                this.pendingSearchCriteria = criteria;
                return;
            }

            this.handleSearchCriteriaChange(criteria);
        },
        filters(filters, oldFilters) {
            if (this.presetItems) {
                return;
            }

            if (JSON.stringify(filters) === JSON.stringify(oldFilters)) {
                return;
            }

            this.reset();
        },
        presetItems(items) {
            if (!Array.isArray(items) || !items.length) {
                return;
            }

            this.setItems(items, { cache: true, initial: true });
            this.syncSelectedFromValue();
        }
    },
    async mounted() {
        if (this.setupOnMounted || this.immediate || this.presetItems || this.value) {
            await this.trySetup();
        }
    },
    methods: {
        reset({ autoSelect = true } = {}) {
            this.resetPagination();
            this.setItems([], { cache: true, initial: true });
            this.setup({ autoSelect });
        },
        async init() {
            if (this.setupLoaded) {
                return;
            }

            this.initialising = true;
            await this.setup();
            await this.wait(200);
            this.initialising = false;
            this.flushPendingSearchCriteria();
        },
        handleSearchCriteriaChange(criteria) {
            // The selected item's text is echoed back into the search input; querying it narrows the
            // list to that one item, leaving no way to pick a different one. Restore the rows already
            // held rather than going back to the server for them.
            if (criteria && criteria === this.selectedText) {
                this.searchDebounced.cancel?.();
                this.searching = false;
                this.setItems(this.$clone(this.initialItems), { cache: true });
                this.rewindToFirstPage(this.items.length);
                return;
            }

            if (!criteria || criteria.length < 2) {
                this.searching = false;
                // Also fires when the value is cleared - auto-selecting here would put back a sole option the
                // user (or the parent) just removed.
                this.reset({ autoSelect: false });
                return;
            }

            this.searchDebounced(criteria);
        },
        flushPendingSearchCriteria() {
            if (this.pendingSearchCriteria === undefined || this.pendingSearchCriteria === null) {
                return;
            }

            const criteria = this.pendingSearchCriteria;
            this.pendingSearchCriteria = undefined;
            this.handleSearchCriteriaChange(criteria);
        },
        async setup({ autoSelect = true } = {}) {
            await this.$nextTick();

            this.searching = false;

            this.searchDebounced = this._.debounce(this.search, 500);

            const filters = {
                ...this.filters,
                ...this.presetFilters
            };

            try {
                if (this.presetItems) {
                    this.setItems(this.presetItems, { cache: true, initial: true });
                    this.syncSelectedFromValue();
                } else {
                    // Send a request to fetch data from a collection, using specified filters and pagination settings.
                    const response = await this.collection.request(null, this.limit, [], filters);

                    // Check if the response is valid and contains an array of data.
                    if (response && typeof response === 'object' && Array.isArray(response.data)) {
                        // Update the component's items with the data from the response.
                        this.setItems(response.data, { cache: true, initial: true });
                        this.setFirstPage(response);

                        // Check if there is a selected value and the items list is empty.
                        if (this.value && !this.items.length) {
                            // Trigger a callback for when an item is selected and reset the selected value.
                            this.onItemSelected();
                            this.selected = null;
                        } else if (this.value && this.items.length) {
                            // Check if there is a selected value and the items list is not empty.
                            if (this.items.some(item => item[`${this.itemKey}`] === this.value)) {
                                // If the selected value exists in the items, trigger the selection callback and update
                                // the selected value.
                                this.onItemSelected(this.items.find(item => item[`${this.itemKey}`] === this.value));
                                this.selected = this.value;
                            } else {
                                // If the selected value does not exist in the items, make another request to check if
                                // it exists.
                                let existing;

                                try {
                                    existing = await this.collection.item(this.value);
                                } catch (error) {
                                    ErrorService.log(error);
                                    existing = await this.collection.request(null, 1, [], {
                                        ...filters,
                                        [this.itemKey]: this.value
                                    });
                                    if (existing && existing.data && existing.data.length) {
                                        existing = existing.data[0];
                                    }
                                }

                                if (existing) {
                                    // If it exists, add it to the beginning of the items list, trigger the selection
                                    // callback, and update the selected value.
                                    this.items.unshift(existing);
                                    this.onItemSelected(existing);
                                    this.selected = this.value;
                                } else {
                                    // If it still doesn't exist, trigger the selection callback and reset the selected
                                    // value.
                                    this.onItemSelected();
                                    this.selected = null;
                                }
                            }
                        } else if (autoSelect && this.autoSelect && !this.value && this.items.length === 1) {
                            // If there is no selected value and there is only one item in the items list, trigger the
                            // selection callback and update the selected
                            this.onItemSelected(this.items[0]);
                            this.selected = this.items[0][`${this.itemKey}`];
                        }
                    }
                }

                this.setupLoaded = true;

                await this.wait(300);
                this.$emit('load', this.items);
            } catch (error) {
                if (error?.code !== 404) {
                    this.handleError(error, this.setup);
                }
            }
        },
        async search(criteria) {
            this.searchLoading = true;

            try {
                this.searchRequests.push(criteria);

                const results = await this.collection.search(
                    criteria,
                    { ...this.filters, ...this.presetFilters },
                    this.uuid
                );

                // A selection or a newer search may have landed while this was in flight.
                if (
                    this.searchRequests[this.searchRequests.length - 1] === criteria &&
                    this.searchCriteria === criteria
                ) {
                    this.setItems(results);
                    this.searching = true;
                }
            } catch (error) {
                this.handleError(error, () => this.search(criteria));
            }

            this.searchLoading = false;
        },
        setItems(items = [], options = { cache: false, initial: false }) {
            this.items = items;

            if (options.initial) {
                this.initialItems = this.$clone(items);
            }

            if (options.cache) {
                this.setCachedItems(items);
            }
        },
        setCachedItems(items = []) {
            if (this.$refs.input && this.$refs.input.$refs.autocomplete) {
                this.$refs.input.$refs.autocomplete.cachedItems = items;
            }
        },
        pushCachedItems(items = []) {
            if (this.$refs.input && this.$refs.input.$refs.autocomplete) {
                this.$refs.input.$refs.autocomplete.cachedItems.push(...items);
            }
        },
        syncSelectedFromValue() {
            if (!this.value || !this.items.length) {
                return;
            }

            const selectedItem = this.items.find(item => item[this.itemKey] === this.value);

            if (!selectedItem) {
                return;
            }

            this.onItemSelected(selectedItem);
            this.selected = this.value;
        },
        onItemSelected(item) {
            this.selectedItemText = item ? this.getItemText(item) : null;

            const itemId = item ? item[`${this.itemKey}`] : null;
            const itemObject = item ? item : null;
            const returnValue = this.returnObject ? itemObject : itemId;

            this.$emit('input', returnValue);
            this.$emit('input:id', itemId);
            this.$emit('input:object', itemObject);

            if (!item) {
                this.$emit('clear');
            }
        },
        getItemText(item) {
            if (typeof this.itemText === 'function') {
                return this.itemText(item);
            }

            return item[`${this.itemText}`];
        },
        getItemDescription(item) {
            if (!this.itemDescription) {
                return null;
            }

            if (typeof this.itemDescription === 'function') {
                return this.itemDescription(item);
            }

            return item[`${this.itemDescription}`];
        },
        getItemTooltip(item) {
            if (!this.itemTooltip) {
                return null;
            }

            if (typeof this.itemTooltip === 'function') {
                try {
                    return this.itemTooltip(item);
                } catch (error) {
                    console.error(error);
                    return null;
                }
            }

            return item[`${this.itemTooltip}`];
        },
        getItemArgs() {
            return [this.value];
        },
        setItemsFromData(data) {
            this.setItems(data, { cache: true, initial: true });

            if (this.items.length === 1) {
                this.onItemSelected(this.items[0]);
                this.selected = this.items[0][`${this.itemKey}`];
            }
        },
        onCreated(response) {
            const item = response.data;
            this.createModal = false;

            this.setItems([item, ...this.items], { cache: true, initial: true });
            this.onItemSelected(item);
            this.selected = item[`${this.itemKey}`];
        },
        onMouseDown(event) {
            if (this.disabled || this.readonly) {
                return;
            }

            if (event.target.closest('button.v-icon')) {
                return;
            }

            this.init();
        }
    }
};
</script>
<style lang="scss">
.field-search {
    &__item {
        color: var(--v-primaryText-base);
        font-size: 0.8125rem;
        padding: 0.4em 0;
        max-width: 32rem;
    }

    &__item-description {
        white-space: normal;
        line-height: 1.3;
    }

    .v-list-item {
        color: var(--v-primaryText-base);
        font-size: 0.8125rem;
        padding: 0.4em 0;
    }
    .v-list-item--dense,
    .v-list--dense .v-list-item {
        min-height: 2rem;
    }
}

.field-search__menu {
    max-width: 32rem !important;
}
</style>
